import { createFileRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { AccountLink } from '../components/AccountLink'
import { ButtonLink, Label, PageTitle } from '../components/ui'
import { meQuery } from '../lib/queries'

// The privacy policy (sc-704). App Store Connect and the iOS app both point
// here, so the path must not move. Every sentence states what the server
// really does (D22, D34, D40, D41): change the code, change this page.
const UPDATED = 'October 12, 2026'

// Where questions go (D41 wants published contact information). The section
// is hidden while this is empty. Forwarded to Ryan by Cloudflare Email Routing
// (sc-704, infra/main.tf).
const CONTACT_EMAIL = 'hello@catbowl.app'

export const Route = createFileRoute('/privacy')({
  // Anyone can read this page, signed in or not (as /about).
  beforeLoad: ({ context }) => context.queryClient.ensureQueryData(meQuery).catch(() => undefined),
  component: Privacy,
})

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="grid gap-2.5">
      <Label>{title}</Label>
      <div className="grid gap-2.5 text-[15px] text-muted">{children}</div>
    </section>
  )
}

function Privacy() {
  return (
    <div className="mx-auto grid w-full max-w-[560px] gap-5 px-4 pb-16 pt-[calc(env(safe-area-inset-top,0px)+24px)]">
      <div className="flex items-start justify-between gap-3">
        <PageTitle eyebrow="Privacy">What we keep, and how to delete it</PageTitle>
        <AccountLink className="mt-1 flex-none" />
      </div>
      <p className="text-[15.5px]">Cat Bowl is a weekly cat photo contest for groups of friends, on the web at catbowl.app and in the iOS app. This page covers both. We collect what the game needs and nothing else: no ads, no analytics services, no selling or sharing of your data.</p>
      <Section title="What we collect">
        <p><b className="font-semibold text-ink">Your account.</b> Your name, your email address, and a password or a passkey. We never store the password itself, only a salted Argon2id hash of it, which can't be turned back into the password. If you sign in with Google or Apple, we receive your name, your email address and an account identifier from them, and nothing else.</p>
        <p><b className="font-semibold text-ink">What you post.</b> Photos and videos, captions, your cats' names, your profile picture, the leagues you belong to, and your votes.</p>
        <p><b className="font-semibold text-ink">Location in your photos.</b> Photos and videos from a phone usually carry where and when they were taken. We keep your original file in private storage that is never served to anyone. What other people see is a re-encoded copy with the location and device details removed.</p>
        <p><b className="font-semibold text-ink">Technical records.</b> Each sign-in records the browser or device it came from. Our server logs requests, including IP addresses, and the app reports a short list of failures (an upload that failed, a script error) with no file names and nothing you typed. We use these only to keep the service working.</p>
      </Section>
      <Section title="Who sees it">
        <p>Only the members of a league see the posts, names and votes in it. Leagues are joined by invitation. If you report a post, the report and your name go to the person who runs the service.</p>
      </Section>
      <Section title="Companies we rely on">
        <p>Railway runs our server and database. Cloudflare stores photos and videos and delivers them. Resend sends our email (verification, password resets, round reminders), so it handles your email address. Google and Apple are involved only if you choose to sign in with them. TypeSafe (typesafe.ai) may help decide when to notify you, so it sees what kinds of notifications are waiting for you, when we last notified you and which notifications you have turned off, but never your name or email address; its terms rule out training on that data. Each handles your data only to provide that service to us.</p>
      </Section>
      <Section title="How long we keep it">
        <p>Until you delete it. Server logs are kept for a short time and then discarded by our hosting provider.</p>
      </Section>
      <Section title="Deleting your account">
        <p>Delete your account from the Account page, on the web or in the app. That removes your account, sign-ins, cats, posts, votes and league memberships, and deletes your photos and videos from storage, originals included. Points other people already earned in finished rounds stay as they are. If you run a league that has other members, the league has to be dealt with first.</p>
      </Section>
      <Section title="Children">
        <p>Cat Bowl is not meant for children under 13, and we don't knowingly collect their information.</p>
      </Section>
      {CONTACT_EMAIL && (
        <Section title="Contact">
          <p>Questions, or a request about your data: <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-ink underline underline-offset-2">{CONTACT_EMAIL}</a>.</p>
        </Section>
      )}
      <p className="text-[13px] text-muted">Last updated {UPDATED}.</p>
      <div className="flex flex-wrap gap-2.5">
        <ButtonLink to="/" size="sm">Back to the league</ButtonLink>
        <ButtonLink to="/terms" size="sm">Terms</ButtonLink>
        <ButtonLink to="/copyright" size="sm">Copyright</ButtonLink>
      </div>
    </div>
  )
}
